import { Store, X, Search, ChevronLeft, ChevronRight, Phone, Bookmark, Download } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IEstablishment } from "../../interfaces/establishment";
import { getDatabaseEstablishments, getEstablishmentTags, getUniqueNafCodes, getNafRelatedTags, exportDatabaseEstablishments, NafCodeOption } from "../../interfaces/establishments";
import { createProspectionList } from "../../interfaces/prospectionLists";
import SignalTable from "../../components/signal/SignalTable";
import SignalTableSkeleton from "../../components/signal/SignalTableSkeleton";
import MultiCityAutocomplete from "../../components/fields/MultiCityAutocomplete";
import { FRENCH_DEPARTMENTS, FRENCH_REGIONS, REGION_TO_DEPARTMENTS } from "../../lib/frenchCities";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";
import AIFilterBar from "../../components/ai/AIFilterBar";
import { AiFilters, resolveGeo } from "../../interfaces/aiFilters";

import TrialPageWall from "../../components/TrialPageWall";
import { NB_ETAB_BUCKETS } from "../../lib/nbEtabBuckets";
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect";
import { FolderIcon } from "@heroicons/react/24/solid";
import { PageHeader } from "../../components/ui/PageHeader";

// A preset selection ("Village", "Métropole") is just a shortcut for a min/max pair — the
// bounds of every checked bucket, merged. Returns empty strings when nothing is checked,
// which is what the min/max inputs show for "no filter".
const presetRange = (
    ids: string[],
    presets: { id: string; min: number; max: number | null }[]
): { min: string; max: string } => {
    const selected = presets.filter(p => ids.includes(p.id));
    if (selected.length === 0) return { min: '', max: '' };
    const min = Math.min(...selected.map(p => p.min));
    const hasUnbounded = selected.some(p => p.max === null);
    const max = hasUnbounded ? null : Math.max(...selected.map(p => p.max as number));
    return { min: String(min), max: max !== null ? String(max) : '' };
};

export default function RestaurantDatabase() {
    const navigate = useNavigate();
    // `?aiPrompt=` is how the client-typing page sends a profile's search prompt here. It is
    // read once into state and dropped from the URL right away, so a refresh (or a bookmark)
    // doesn't re-run a search the user has since changed by hand.
    const [searchParams, setSearchParams] = useSearchParams();
    const [handedOverPrompt] = useState<string>(() => searchParams.get("aiPrompt") ?? "");
    const [establishments, setEstablishments] = useState<IEstablishment[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const isTrialUser = localStorage.getItem("isSub") !== "true" && localStorage.getItem("isAdmin") !== "true" && !!localStorage.getItem("trialEndsAt");
    const isDuckflowAdmin = localStorage.getItem("isAdmin") === "true";
    const trialPageLimit = 5;
    const [showTrialWall, setShowTrialWall] = useState(false);

    // Export modal
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportOpeningDateEnabled, setExportOpeningDateEnabled] = useState(false);
    const [exportMinOpeningDate, setExportMinOpeningDate] = useState("");
    const [exportCreationDateEnabled, setExportCreationDateEnabled] = useState(false);
    const [exportMinCreationDate, setExportMinCreationDate] = useState("");
    const [exportHygieneControlEnabled, setExportHygieneControlEnabled] = useState(false);
    const [exportHygieneControlDate, setExportHygieneControlDate] = useState("");
    const [exportLimit, setExportLimit] = useState("200");
    const [exportCleanLines, setExportCleanLines] = useState("");
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");

    // Save list modal
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveListName, setSaveListName] = useState("");
    const [saveListDescription, setSaveListDescription] = useState("");
    const [saveListType, setSaveListType] = useState<"static" | "dynamic">("dynamic");
    const [savingList, setSavingList] = useState(false);

    const STORAGE_KEY = "restaurantDatabase_filters";
    const isMounted = useRef(false);
    // Filter dropdowns flip left/right based on the left edge of the
    // "Enregistrer comme liste" button (pinned to the end of the toolbar row
    // via ml-auto) — a dropdown can fit within the viewport while still
    // overlapping that button, so the viewport edge alone isn't enough.
    const saveButtonRef = useRef<HTMLButtonElement>(null);

    const loadSavedState = () => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) return JSON.parse(saved);
        } catch {}
        return null;
    };

    const savedState = loadSavedState();

    // Filters
    const [searchQuery, setSearchQuery] = useState<string>(savedState?.searchQuery ?? "");
    const [page, setPage] = useState<number>(savedState?.page ?? 1);
    const itemsPerPage = 20;
    const [minReviews, setMinReviews] = useState<string>(savedState?.minReviews ?? '');
    const [cityFilters, setCityFilters] = useState<string[]>(savedState?.cityFilters ?? []);
    const [departmentFilters, setDepartmentFilters] = useState<string[]>(savedState?.departmentFilters ?? []);
    const [regionFilters, setRegionFilters] = useState<string[]>(savedState?.regionFilters ?? []);
    const [minRating, setMinRating] = useState<string>(savedState?.minRating ?? '');
    const [contactFilters, setContactFilters] = useState<string[]>(savedState?.contactFilters ?? []);
    const [networkStatus, setNetworkStatus] = useState<string[]>(savedState?.networkStatus ?? []);
    const [establishmentType, setEstablishmentType] = useState<string[]>(savedState?.establishmentType ?? []);
    const [concept, setConcept] = useState<string[]>(savedState?.concept ?? []);
    const [phoneTypeFilters, setPhoneTypeFilters] = useState<('mobile' | 'landline')[]>(savedState?.phoneTypeFilters ?? []);
    const [emailTypeFilters, setEmailTypeFilters] = useState<('owner' | 'business')[]>(savedState?.emailTypeFilters ?? []);
    const [selectedPopulationPresets, setSelectedPopulationPresets] = useState<string[]>(savedState?.selectedPopulationPresets ?? []);
    const [customMinPop, setCustomMinPop] = useState<string>(savedState?.customMinPop ?? '');
    const [customMaxPop, setCustomMaxPop] = useState<string>(savedState?.customMaxPop ?? '');
    const [selectedM2Presets, setSelectedM2Presets] = useState<string[]>(savedState?.selectedM2Presets ?? []);
    const [customMinM2, setCustomMinM2] = useState<string>(savedState?.customMinM2 ?? '');
    const [customMaxM2, setCustomMaxM2] = useState<string>(savedState?.customMaxM2 ?? '');
    // `?nafCode=` is how a "Répartition par activité" bar on the dashboard hands off a
    // click-through — takes priority over whatever was saved from the last session, since
    // that's the whole point of following the link, but leaves every other saved filter
    // (city, etc.) alone.
    const [nafCodeFilters, setNafCodeFilters] = useState<string[]>(() => {
        const fromUrl = searchParams.get("nafCode");
        return fromUrl ? [fromUrl] : (savedState?.nafCodeFilters ?? []);
    });
    const [companySizeFilters, setCompanySizeFilters] = useState<string[]>(savedState?.companySizeFilters ?? []);
    const [legalFormFilters, setLegalFormFilters] = useState<string[]>(savedState?.legalFormFilters ?? []);
    const [controlResultFilter, setControlResultFilter] = useState<string>(savedState?.controlResultFilter ?? '');
    const [controlFollowUpFilter, setControlFollowUpFilter] = useState<string>(savedState?.controlFollowUpFilter ?? '');
    const [ageFilter, setAgeFilter] = useState<string>(savedState?.ageFilter ?? '');
    const [nbEtabFilters, setNbEtabFilters] = useState<string[]>(savedState?.nbEtabFilters ?? []);
    const [openingDateFrom, setOpeningDateFrom] = useState<string>(savedState?.openingDateFrom ?? '');
    const [openingDateTo, setOpeningDateTo] = useState<string>(savedState?.openingDateTo ?? '');
    const [openingDateSort, setOpeningDateSort] = useState<'asc' | 'desc' | null>(null);

    const cycleOpeningDateSort = () => {
        setOpeningDateSort(prev => prev === null ? 'desc' : prev === 'desc' ? 'asc' : null);
    };

    // Dropdown states
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [_showNetworkDropdown, setShowNetworkDropdown] = useState(false);
    const [showEstablishmentDropdown, setShowEstablishmentDropdown] = useState(false);
    const [_showConceptDropdown, setShowConceptDropdown] = useState(false);
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [showNafDropdown, setShowNafDropdown] = useState(false);
    const [showCompanySizeDropdown, setShowCompanySizeDropdown] = useState(false);
    const [showLegalFormDropdown, setShowLegalFormDropdown] = useState(false);
    const [showControlResultDropdown, setShowControlResultDropdown] = useState(false);
    const [showControlFollowUpDropdown, setShowControlFollowUpDropdown] = useState(false);
    const [showAgeDropdown, setShowAgeDropdown] = useState(false);
    const [showNbEtabDropdown, setShowNbEtabDropdown] = useState(false);
    const [showOpeningDateDropdown, setShowOpeningDateDropdown] = useState(false);
    const [departmentSearch, setDepartmentSearch] = useState('');
    const [showRegionDropdown, setShowRegionDropdown] = useState(false);
    const [showPopulationDropdown, setShowPopulationDropdown] = useState(false);
    const [showM2Dropdown, setShowM2Dropdown] = useState(false);
    const [regionSearch, setRegionSearch] = useState('');
    const [_conceptSearch, _setConceptSearch] = useState('');

    // Edge-aware alignment: flip each dropdown to the right edge of its trigger
    // when it would otherwise overflow past the right side of the viewport.
    const departmentDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showDepartmentDropdown, saveButtonRef, "left");
    const regionDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showRegionDropdown, saveButtonRef, "left");
    const populationDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showPopulationDropdown, saveButtonRef, "left");
    const m2DropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showM2Dropdown, saveButtonRef, "left");
    const contactDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showContactDropdown, saveButtonRef, "left");
    const networkDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(_showNetworkDropdown, saveButtonRef, "left");
    const establishmentDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showEstablishmentDropdown, saveButtonRef, "left");
    const nafDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showNafDropdown, saveButtonRef, "left");
    const companySizeDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showCompanySizeDropdown, saveButtonRef, "left");
    const legalFormDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showLegalFormDropdown, saveButtonRef, "left");
    const controlResultDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showControlResultDropdown, saveButtonRef, "left");
    const controlFollowUpDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showControlFollowUpDropdown, saveButtonRef, "left");
    const ageDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showAgeDropdown, saveButtonRef, "left");
    const nbEtabDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showNbEtabDropdown, saveButtonRef, "left");
    const openingDateDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showOpeningDateDropdown, saveButtonRef, "left");
    const conceptDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(_showConceptDropdown, saveButtonRef, "left");

    // Dynamic tags from database
    const [availableCategories, setAvailableCategories] = useState<string[]>([]);
    const [_availableSubCategories, setAvailableSubCategories] = useState<string[]>([]);
    const [availableNafCodes, setAvailableNafCodes] = useState<NafCodeOption[]>([]);
    const [nafSearch, setNafSearch] = useState('');

    const CITY_SIZE_PRESETS: { id: string; label: string; min: number; max: number | null }[] = [
        { id: "hamlet",     label: "Hameau",       min: 0,      max: 499    },
        { id: "village",    label: "Village",       min: 500,    max: 1999   },
        { id: "town",       label: "Bourg",         min: 2000,   max: 9999   },
        { id: "small_city", label: "Petite ville",  min: 10000,  max: 49999  },
        { id: "city",       label: "Ville",         min: 50000,  max: 299999 },
        { id: "metropolis", label: "Métropole",     min: 300000, max: null   },
    ];

    const hasPopulationFilter = selectedPopulationPresets.length > 0 || customMinPop || customMaxPop;

    const M2_PRICE_PRESETS: { id: string; label: string; min: number; max: number | null }[] = [
        { id: "low_cost",       label: "Low-cost",         min: 0,    max: 1500 },
        { id: "accessible",     label: "Accessible",       min: 1500, max: 2500 },
        { id: "competitive",    label: "Compétitif",       min: 2500, max: 3500 },
        { id: "high_footfall",  label: "Forte affluence",  min: 3500, max: 7000 },
        { id: "premium_street", label: "Premium",          min: 7000, max: null },
    ];

    const hasM2Filter = selectedM2Presets.length > 0 || customMinM2 || customMaxM2;

    const establishmentTypeOptions = availableCategories.map(cat => ({ value: cat, label: cat }));

    const _networkStatusOptions = [
        { value: 'independant', label: 'Indépendant' },
        { value: 'franchise', label: 'Franchise' },
        { value: 'groupe', label: 'Groupe' },
    ];

    const contactLabels: Record<string, string> = {
        'linkedin': 'LinkedIn',
        'instagram': 'Instagram',
        'email': 'Email',
        'phone': 'Téléphone',
        'website': 'Site web'
    };

    // Both handoffs have already been captured into state above; take them out of the URL
    // so each is applied exactly once. `replace` keeps them out of the history too.
    useEffect(() => {
        if (!searchParams.get("aiPrompt") && !searchParams.get("nafCode")) return;
        const next = new URLSearchParams(searchParams);
        next.delete("aiPrompt");
        next.delete("nafCode");
        setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load unique tags
    useEffect(() => {
        getEstablishmentTags()
            .then(tags => {
                setAvailableCategories(tags.categories || []);
                setAvailableSubCategories(tags.subCategories || []);
            })
            .catch(err => console.error('Failed to load tags:', err));

        getUniqueNafCodes()
            .then(codes => setAvailableNafCodes(codes || []))
            .catch(err => console.error('Failed to load NAF codes:', err));
    }, []);

    // Selecting a NAF code auto-selects any category/subCategory tag that
    // actually co-occurs with it in the data (real backend search, not a static mapping)
    const handleNafToggle = (code: string, checked: boolean) => {
        setNafCodeFilters(checked ? [...nafCodeFilters, code] : nafCodeFilters.filter(c => c !== code));
        if (checked) {
            getNafRelatedTags(code)
                .then(({ categories, subCategories }) => {
                    if (categories.length) {
                        setEstablishmentType(prev => Array.from(new Set([...prev, ...categories])));
                    }
                    if (subCategories.length) {
                        setConcept(prev => Array.from(new Set([...prev, ...subCategories])));
                    }
                })
                .catch(err => console.error('Failed to load related tags for NAF', code, err));
        }
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.relative')) {
                setShowContactDropdown(false);
                setShowNetworkDropdown(false);
                setShowEstablishmentDropdown(false);
                setShowConceptDropdown(false);
                setShowDepartmentDropdown(false);
                setShowRegionDropdown(false);
                setShowPopulationDropdown(false);
                setShowM2Dropdown(false);
                setShowNafDropdown(false);
                setShowCompanySizeDropdown(false);
                setShowLegalFormDropdown(false);
                setShowControlResultDropdown(false);
                setShowControlFollowUpDropdown(false);
                setShowAgeDropdown(false);
                setShowNbEtabDropdown(false);
                setShowOpeningDateDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Persist filters and page to localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            searchQuery, page, minReviews, cityFilters, departmentFilters, regionFilters, minRating,
            contactFilters, networkStatus, establishmentType, concept, phoneTypeFilters, emailTypeFilters,
            selectedPopulationPresets, customMinPop, customMaxPop,
            selectedM2Presets, customMinM2, customMaxM2,
            nafCodeFilters, companySizeFilters, legalFormFilters,
            controlResultFilter, controlFollowUpFilter, ageFilter, nbEtabFilters,
            openingDateFrom, openingDateTo,
        }));
    }, [searchQuery, page, minReviews, cityFilters, departmentFilters, regionFilters, minRating, contactFilters, networkStatus, establishmentType, concept, phoneTypeFilters, emailTypeFilters, selectedPopulationPresets, customMinPop, customMaxPop, selectedM2Presets, customMinM2, customMaxM2, nafCodeFilters, companySizeFilters, legalFormFilters, controlResultFilter, controlFollowUpFilter, ageFilter, nbEtabFilters, openingDateFrom, openingDateTo]);

    // Reset page when filters change (skip on initial mount to preserve saved page)
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        setPage(1);
    }, [searchQuery, minReviews, cityFilters, departmentFilters, regionFilters, minRating, contactFilters, networkStatus, establishmentType, concept, phoneTypeFilters, emailTypeFilters, customMinPop, customMaxPop, customMinM2, customMaxM2, nafCodeFilters, companySizeFilters, legalFormFilters, controlResultFilter, controlFollowUpFilter, ageFilter, nbEtabFilters, openingDateFrom, openingDateTo, openingDateSort]);

    // Presets → custom inputs. Done on the click rather than in an effect on
    // `selectedPopulationPresets`: an effect also fires when something *else* empties the
    // presets — the AI bar setting an explicit range, for instance — and would wipe the
    // min/max it had just written.
    const applyPopulationPresets = (ids: string[]) => {
        setSelectedPopulationPresets(ids);
        const { min, max } = presetRange(ids, CITY_SIZE_PRESETS);
        setCustomMinPop(min);
        setCustomMaxPop(max);
    };

    const applyM2Presets = (ids: string[]) => {
        setSelectedM2Presets(ids);
        const { min, max } = presetRange(ids, M2_PRICE_PRESETS);
        setCustomMinM2(min);
        setCustomMaxM2(max);
    };

    // Fetch establishments
    useDebouncedEffect(() => {
        fetchEstablishments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchQuery, minReviews, cityFilters, departmentFilters, regionFilters, minRating, contactFilters, networkStatus, establishmentType, concept, phoneTypeFilters, emailTypeFilters, customMinPop, customMaxPop, customMinM2, customMaxM2, nafCodeFilters, companySizeFilters, legalFormFilters, controlResultFilter, controlFollowUpFilter, ageFilter, nbEtabFilters, openingDateFrom, openingDateTo, openingDateSort]);

    const fetchEstablishments = async () => {
        try {
            setLoading(true);
            const params: any = {
                page,
                limit: itemsPerPage,
            };

            if (searchQuery) {
                const cleanQuery = searchQuery.trim().replace(/\s+/g, '');
                if (/^\d+$/.test(cleanQuery)) {
                    params.siren = cleanQuery;
                } else {
                    params.search = searchQuery;
                }
            }
            if (cityFilters.length > 0) params.city = cityFilters.join(',');
            const regionDepts = regionFilters.flatMap(r => REGION_TO_DEPARTMENTS[r] ?? []);
            const allDepts = [...new Set([...departmentFilters, ...regionDepts])];
            if (allDepts.length > 0) params.departments = allDepts.join(',');
            if (minReviews) params.minReviews = minReviews;
            if (minRating) params.minRating = minRating;
            if (contactFilters.length > 0) params.contactFilters = contactFilters.join(',');
            if (networkStatus.length > 0) params.networkStatus = networkStatus.join(',');
            if (establishmentType.length > 0) params.establishmentType = establishmentType.join(',');
            if (concept.length > 0) params.concept = concept.join(',');
            if (phoneTypeFilters && phoneTypeFilters.length > 0) params.phoneType = phoneTypeFilters.join(',');
            if (emailTypeFilters && emailTypeFilters.length > 0) params.emailType = emailTypeFilters.join(',');
            if (customMinPop) params.minPopulation = customMinPop;
            if (customMaxPop) params.maxPopulation = customMaxPop;
            if (customMinM2) params.minMedianM2 = customMinM2;
            if (customMaxM2) params.maxMedianM2 = customMaxM2;
            if (nafCodeFilters.length > 0) params.nafCode = nafCodeFilters.join(',');
            if (companySizeFilters.length > 0) params.workforceRange = companySizeFilters.join(',');
            if (legalFormFilters.length > 0) params.legalForm = legalFormFilters.join(',');
            if (controlResultFilter) params.controlResult = controlResultFilter;
            if (controlFollowUpFilter) params.controlFollowUp = controlFollowUpFilter;
            if (ageFilter) params.ageFilter = ageFilter;
            if (nbEtabFilters.length > 0) params.nbEtablissements = nbEtabFilters.join(',');
            if (openingDateFrom) params.openingDateFrom = openingDateFrom;
            if (openingDateTo) params.openingDateTo = openingDateTo;
            if (openingDateSort) params.openingDateSort = openingDateSort;

            const response = await getDatabaseEstablishments(params);
            setEstablishments(response.establishments || []);
            setTotal(response.total || 0);
        } catch (error) {
            console.error("Failed to fetch establishments:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveToggle = (id: string, saved: boolean) => {
        const userId = localStorage.getItem("userId") || "";
        setEstablishments(prev =>
            prev.map(est => {
                if ((est as any)._id !== id) return est;
                const savedBy: string[] = (est as any).savedBy || [];
                return {
                    ...est,
                    savedBy: saved ? [...savedBy, userId] : savedBy.filter((s: string) => s !== userId),
                } as any;
            })
        );
    };

    const handleSaveList = async () => {
        if (!saveListName.trim()) return;
        setSavingList(true);
        try {
            const filters: Record<string, any> = { source: "database" };
            if (searchQuery) filters.search = searchQuery;
            if (cityFilters.length > 0) filters.city = cityFilters.join(',');
            const regionDepts2 = regionFilters.flatMap(r => REGION_TO_DEPARTMENTS[r] ?? []);
            const allDepts2 = [...new Set([...departmentFilters, ...regionDepts2])];
            if (allDepts2.length > 0) filters.departments = allDepts2.join(',');
            if (minRating) filters.minRating = minRating;
            if (minReviews) filters.minReviews = minReviews;
            if (contactFilters.length > 0) filters.contactFilters = contactFilters;
            if (networkStatus.length > 0) filters.networkStatus = networkStatus;
            if (establishmentType.length > 0) filters.establishmentType = establishmentType;
            if (concept.length > 0) filters.concept = concept;
            if (phoneTypeFilters.length > 0) filters.phoneType = phoneTypeFilters.join(',');
            if (emailTypeFilters.length > 0) filters.emailType = emailTypeFilters.join(',');
            if (customMinPop) filters.minPopulation = customMinPop;
            if (customMaxPop) filters.maxPopulation = customMaxPop;
            if (customMinM2) filters.minMedianM2 = customMinM2;
            if (customMaxM2) filters.maxMedianM2 = customMaxM2;
            if (nafCodeFilters.length > 0) filters.nafCode = nafCodeFilters.join(',');
            if (companySizeFilters.length > 0) filters.workforceRange = companySizeFilters.join(',');
            if (legalFormFilters.length > 0) filters.legalForm = legalFormFilters.join(',');
            if (controlResultFilter) filters.controlResult = controlResultFilter;
            if (controlFollowUpFilter) filters.controlFollowUp = controlFollowUpFilter;
            if (ageFilter) filters.ageFilter = ageFilter;
            if (nbEtabFilters.length > 0) filters.nbEtablissements = nbEtabFilters.join(',');
            if (openingDateFrom) filters.openingDateFrom = openingDateFrom;
            if (openingDateTo) filters.openingDateTo = openingDateTo;

            const list = await createProspectionList({
                name: saveListName.trim(),
                description: saveListDescription.trim(),
                type: saveListType,
                filters,
            });
            setShowSaveModal(false);
            setSaveListName("");
            setSaveListDescription("");
            setSaveListType("dynamic");
            navigate(`/prospection/${list._id}`);
        } catch (err) {
            console.error("Failed to save list:", err);
        } finally {
            setSavingList(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        setExportError("");
        try {
            const params: Record<string, any> = {};
            if (searchQuery) { const clean = searchQuery.trim().replace(/\s+/g, ''); if (/^\d+$/.test(clean)) params.siren = clean; else params.search = searchQuery; }
            if (cityFilters.length > 0) params.city = cityFilters.join(',');
            const regionDepts = regionFilters.flatMap(r => REGION_TO_DEPARTMENTS[r] ?? []);
            const allDepts = [...new Set([...departmentFilters, ...regionDepts])];
            if (allDepts.length > 0) params.departments = allDepts.join(',');
            if (minReviews) params.minReviews = minReviews;
            if (minRating) params.minRating = minRating;
            if (contactFilters.length > 0) params.contactFilters = contactFilters.join(',');
            if (networkStatus.length > 0) params.networkStatus = networkStatus.join(',');
            if (establishmentType.length > 0) params.establishmentType = establishmentType.join(',');
            if (concept.length > 0) params.concept = concept.join(',');
            if (phoneTypeFilters.length > 0) params.phoneType = phoneTypeFilters.join(',');
            if (emailTypeFilters.length > 0) params.emailType = emailTypeFilters.join(',');
            if (customMinPop) params.minPopulation = customMinPop;
            if (customMaxPop) params.maxPopulation = customMaxPop;
            if (customMinM2) params.minMedianM2 = customMinM2;
            if (customMaxM2) params.maxMedianM2 = customMaxM2;
            if (nafCodeFilters.length > 0) params.nafCode = nafCodeFilters.join(',');
            if (companySizeFilters.length > 0) params.workforceRange = companySizeFilters.join(',');
            if (legalFormFilters.length > 0) params.legalForm = legalFormFilters.join(',');
            if (controlResultFilter) params.controlResult = controlResultFilter;
            if (controlFollowUpFilter) params.controlFollowUp = controlFollowUpFilter;
            if (ageFilter) params.ageFilter = ageFilter;
            if (nbEtabFilters.length > 0) params.nbEtablissements = nbEtabFilters.join(',');
            if (exportOpeningDateEnabled && exportMinOpeningDate) params.minOpeningDate = exportMinOpeningDate;
            if (exportCreationDateEnabled && exportMinCreationDate) params.minCreationDate = exportMinCreationDate;
            if (exportHygieneControlEnabled && exportHygieneControlDate) params.minHygieneControlDate = exportHygieneControlDate;
            if (exportLimit) params.exportLimit = exportLimit;
            if (exportCleanLines !== "") params.cleanLines = exportCleanLines;
            await exportDatabaseEstablishments(params);
            setShowExportModal(false);
        } catch {
            setExportError("Une erreur est survenue lors de l'export. Veuillez réessayer.");
        } finally {
            setExporting(false);
        }
    };

    const clearAllFilters = () => {
        setSearchQuery("");
        setMinReviews('');
        setCityFilters([]);
        setDepartmentFilters([]);
        setRegionFilters([]);
        setMinRating('');
        setContactFilters([]);
        setNetworkStatus([]);
        setEstablishmentType([]);
        setConcept([]);
        setPhoneTypeFilters([]);
        setEmailTypeFilters([]);
        setSelectedPopulationPresets([]);
        setCustomMinPop('');
        setCustomMaxPop('');
        setSelectedM2Presets([]);
        setCustomMinM2('');
        setCustomMaxM2('');
        setAgeFilter('');
        setNbEtabFilters([]);
        setOpeningDateFrom('');
        setOpeningDateTo('');
    };

    /**
     * Apply a filter set returned by the AI bar.
     *
     * Every filter is written, including the ones the AI left out (as empty) — a description
     * describes the whole search, so what is not in it must not survive from the previous one.
     * Population and loyer ranges land in the custom min/max inputs rather than the preset
     * checkboxes: the AI works in real numbers, which rarely line up with a bucket's bounds.
     */
    const applyAiFilters = (f: AiFilters) => {
        const geo = resolveGeo(f);
        // `searchQuery` is deliberately untouched: that box is the manual "find this restaurant
        // by name" lookup, so the assistant neither writes into it nor clears what is typed
        // there. The server has no `search` field either — a named enseigne comes back as
        // unsupported instead.
        setCityFilters(f.cities ?? []);
        setDepartmentFilters(geo.departments);
        setRegionFilters(geo.regions);
        setEstablishmentType(f.establishmentType ?? []);
        setConcept(f.concept ?? []);
        setNafCodeFilters(f.nafCode ?? []);
        setMinRating(f.minRating != null ? String(f.minRating) : '');
        setMinReviews(f.minReviews != null ? String(f.minReviews) : '');
        setContactFilters(f.contactFilters ?? []);
        setPhoneTypeFilters(f.phoneType ?? []);
        setEmailTypeFilters(f.emailType ?? []);
        setCompanySizeFilters(f.workforceRange ?? []);
        setLegalFormFilters(f.legalForm ?? []);
        setAgeFilter(f.ageFilter ?? '');
        setNbEtabFilters(f.nbEtablissements ?? []);
        setNetworkStatus([]);
        setSelectedPopulationPresets([]);
        setCustomMinPop(f.minPopulation != null ? String(f.minPopulation) : '');
        setCustomMaxPop(f.maxPopulation != null ? String(f.maxPopulation) : '');
        setSelectedM2Presets([]);
        setCustomMinM2(f.minMedianM2 != null ? String(f.minMedianM2) : '');
        setCustomMaxM2(f.maxMedianM2 != null ? String(f.maxMedianM2) : '');
        setOpeningDateFrom(f.openingDateFrom ?? '');
        setOpeningDateTo(f.openingDateTo ?? '');
        setControlResultFilter(f.controlResult ?? '');
        setControlFollowUpFilter(f.controlFollowUp ?? '');
        setPage(1);
    };

    const activeFilterCount = [
        minReviews, minRating, searchQuery, ...cityFilters, ...departmentFilters, ...regionFilters,
        phoneTypeFilters.join(',') || '',
        emailTypeFilters.join(',') || '',
        ...contactFilters, ...networkStatus, ...establishmentType, ...concept,
        hasPopulationFilter ? '1' : '',
        hasM2Filter ? '1' : '',
    ].filter(Boolean).length;

    const totalPages = Math.ceil(total / itemsPerPage);

    const goToPage = (pageNumber: number) => {
        if (isTrialUser && pageNumber > trialPageLimit) {
            setShowTrialWall(true);
            return;
        }
        setPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;
        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            let startPage = Math.max(2, page - 1);
            let endPage = Math.min(totalPages - 1, page + 1);
            if (startPage > 2) pages.push('...');
            for (let i = startPage; i <= endPage; i++) pages.push(i);
            if (endPage < totalPages - 1) pages.push('...');
            if (totalPages > 1) pages.push(totalPages);
        }
        return pages;
    };

    return (
        <>
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 pb-6">
                <PageHeader icon={<FolderIcon />} title="Database" subtitle="Triés par richesse des informations de contact" />

                {/* Real-time indicator */}
                <div className="flex items-center gap-2 mb-6">
                    <span className="relative flex h-2 w-2">
                        <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                    </span>
                    <span className="text-sm font-medium text-ink-muted">Données en temps réel</span>
                </div>

                {/* AI filter bar */}
                <AIFilterBar context="database" onApply={applyAiFilters} initialPrompt={handedOverPrompt} className="mb-4" />

                {/* Filters Bar */}
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-1 flex-wrap">
                        {/* Search */}
                        <div className="relative flex items-center">
                            <Search className="w-4 h-4 text-ink-faint absolute left-2" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher un restaurant..."
                                className="h-8 pl-7 pr-2.5 text-xs rounded-lg bg-surface border border-border hover:border-border-strong focus:border-accent focus:ring-1 focus:ring-accent w-64 transition-colors"
                            />
                        </div>

                        {/* Villes */}
                        <MultiCityAutocomplete
                            values={cityFilters}
                            onChange={setCityFilters}
                        />

                        {/* Département */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowDepartmentDropdown(!showDepartmentDropdown);
                                    setShowRegionDropdown(false);
                                    setShowContactDropdown(false);
                                    setShowEstablishmentDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                    departmentFilters.length > 0
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                            >
                                <span>Département</span>
                                {departmentFilters.length > 0 && (
                                    <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                        {departmentFilters.length}
                                    </span>
                                )}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showDepartmentDropdown && (
                                <div ref={departmentDropdownAlign.ref} className={`absolute top-full mt-1 ${departmentDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-56 flex flex-col`} style={{ maxHeight: 280 }}>
                                    <div className="px-2 py-1.5 border-b border-border shrink-0">
                                        <input
                                            type="text"
                                            value={departmentSearch}
                                            onChange={(e) => setDepartmentSearch(e.target.value)}
                                            placeholder="Rechercher un département..."
                                            className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                        {FRENCH_DEPARTMENTS.filter(d => {
                                            const q = departmentSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                            const name = d.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                            return !q || d.code.toLowerCase().startsWith(q) || name.includes(q);
                                        }).map(dep => (
                                            <label key={dep.code} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={departmentFilters.includes(dep.code)}
                                                    onChange={(e) => {
                                                        setDepartmentFilters(e.target.checked
                                                            ? [...departmentFilters, dep.code]
                                                            : departmentFilters.filter(c => c !== dep.code)
                                                        );
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{dep.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Région */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowRegionDropdown(!showRegionDropdown);
                                    setShowDepartmentDropdown(false);
                                    setShowContactDropdown(false);
                                    setShowEstablishmentDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                    regionFilters.length > 0
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                            >
                                <span>Région</span>
                                {regionFilters.length > 0 && (
                                    <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                        {regionFilters.length}
                                    </span>
                                )}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showRegionDropdown && (
                                <div ref={regionDropdownAlign.ref} className={`absolute top-full mt-1 ${regionDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-64 flex flex-col`} style={{ maxHeight: 280 }}>
                                    <div className="px-2 py-1.5 border-b border-border shrink-0">
                                        <input
                                            type="text"
                                            value={regionSearch}
                                            onChange={(e) => setRegionSearch(e.target.value)}
                                            placeholder="Rechercher une région..."
                                            className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                    <div className="overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                        {FRENCH_REGIONS.filter(r => {
                                            const q = regionSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                            const name = r.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
                                            return !q || name.includes(q);
                                        }).map(region => (
                                            <label key={region} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={regionFilters.includes(region)}
                                                    onChange={(e) => {
                                                        setRegionFilters(e.target.checked
                                                            ? [...regionFilters, region]
                                                            : regionFilters.filter(r => r !== region)
                                                        );
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{region}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Population */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowPopulationDropdown(!showPopulationDropdown);
                                    setShowM2Dropdown(false);
                                    setShowDepartmentDropdown(false);
                                    setShowRegionDropdown(false);
                                    setShowContactDropdown(false);
                                    setShowEstablishmentDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                    hasPopulationFilter
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                            >
                                <span>Population</span>
                                {hasPopulationFilter && (
                                    <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                        {selectedPopulationPresets.length || 1}
                                    </span>
                                )}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showPopulationDropdown && (
                                <div ref={populationDropdownAlign.ref} className={`absolute top-full mt-1 ${populationDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-56 py-1`}>
                                    {CITY_SIZE_PRESETS.map(preset => (
                                        <label key={preset.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedPopulationPresets.includes(preset.id)}
                                                onChange={e => {
                                                    applyPopulationPresets(e.target.checked
                                                        ? [...selectedPopulationPresets, preset.id]
                                                        : selectedPopulationPresets.filter(id => id !== preset.id)
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className={`text-xs flex-1 ${selectedPopulationPresets.includes(preset.id) ? 'font-semibold text-ink' : 'text-ink-muted'}`}>{preset.label}</span>
                                            <span className="text-ink-faint text-[10px]">
                                                {preset.max !== null
                                                    ? `${preset.min.toLocaleString('fr-FR')} – ${preset.max.toLocaleString('fr-FR')}`
                                                    : `+${preset.min.toLocaleString('fr-FR')}`}
                                            </span>
                                        </label>
                                    ))}
                                    <div className="border-t border-border mt-1 pt-2 px-3 pb-2 space-y-1.5">
                                        <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide mb-1">Personnalisé</p>
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                type="number"
                                                value={customMinPop}
                                                onChange={e => { setSelectedPopulationPresets([]); setCustomMinPop(e.target.value); }}
                                                placeholder="Min"
                                                className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <span className="text-ink-faint text-xs shrink-0">–</span>
                                            <input
                                                type="number"
                                                value={customMaxPop}
                                                onChange={e => { setSelectedPopulationPresets([]); setCustomMaxPop(e.target.value); }}
                                                placeholder="Max"
                                                className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Loyer médian m² */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowM2Dropdown(!showM2Dropdown);
                                    setShowPopulationDropdown(false);
                                    setShowDepartmentDropdown(false);
                                    setShowRegionDropdown(false);
                                    setShowContactDropdown(false);
                                    setShowEstablishmentDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                    hasM2Filter
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                            >
                                <span>Loyer m²</span>
                                {hasM2Filter && (
                                    <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                        {selectedM2Presets.length + (customMinM2 || customMaxM2 ? 1 : 0)}
                                    </span>
                                )}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showM2Dropdown && (
                                <div ref={m2DropdownAlign.ref} className={`absolute top-full mt-1 ${m2DropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-60 py-1`}>
                                    {M2_PRICE_PRESETS.map(preset => (
                                        <label key={preset.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedM2Presets.includes(preset.id)}
                                                onChange={e => {
                                                    applyM2Presets(e.target.checked
                                                        ? [...selectedM2Presets, preset.id]
                                                        : selectedM2Presets.filter(id => id !== preset.id)
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className={`text-xs flex-1 ${selectedM2Presets.includes(preset.id) ? 'font-semibold text-ink' : 'text-ink-muted'}`}>{preset.label}</span>
                                            <span className="text-ink-faint text-[10px]">
                                                {preset.max !== null
                                                    ? `${preset.min.toLocaleString('fr-FR')} – ${preset.max.toLocaleString('fr-FR')} €`
                                                    : `+${preset.min.toLocaleString('fr-FR')} €`}
                                            </span>
                                        </label>
                                    ))}
                                    <div className="border-t border-border mt-1 pt-2 px-3 pb-2 space-y-1.5">
                                        <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide mb-1">Personnalisé (€/m²)</p>
                                        <div className="flex items-center gap-1.5">
                                            <input
                                                type="number"
                                                value={customMinM2}
                                                onChange={e => { setSelectedM2Presets([]); setCustomMinM2(e.target.value); }}
                                                placeholder="Min"
                                                className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <span className="text-ink-faint text-xs shrink-0">–</span>
                                            <input
                                                type="number"
                                                value={customMaxM2}
                                                onChange={e => { setSelectedM2Presets([]); setCustomMaxM2(e.target.value); }}
                                                placeholder="Max"
                                                className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                                onClick={e => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Coordonnées */}
                        <div className="relative">
                            <button
                                onClick={() => {
                                    setShowContactDropdown(!showContactDropdown);
                                    setShowNetworkDropdown(false);
                                    setShowEstablishmentDropdown(false);
                                    setShowConceptDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                    contactFilters.length > 0 || phoneTypeFilters.length > 0 || emailTypeFilters.length > 0
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                            >
                                <span>Coordonnées</span>
                                {(contactFilters.length > 0 || phoneTypeFilters.length > 0 || emailTypeFilters.length > 0) && (
                                    <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                        {contactFilters.length + phoneTypeFilters.length + emailTypeFilters.length}
                                    </span>
                                )}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showContactDropdown && (
                                <div ref={contactDropdownAlign.ref} className={`absolute top-full mt-1 ${contactDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[180px] py-1`}>
                                    <div className="px-3 py-1">
                                        <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Présence en ligne</span>
                                    </div>
                                    {['linkedin', 'instagram', 'website'].map(contact => (
                                        <label key={contact} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={contactFilters.includes(contact)}
                                                onChange={(e) => {
                                                    setContactFilters(e.target.checked
                                                        ? [...contactFilters, contact]
                                                        : contactFilters.filter(f => f !== contact)
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted">{contactLabels[contact] || contact}</span>
                                        </label>
                                    ))}
                                    
                                    <div className="border-t border-border my-1"></div>
                                    <div className="px-3 py-1">
                                        <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Type de téléphone</span>
                                    </div>
                                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={phoneTypeFilters.includes('mobile')}
                                            onChange={(e) => {
                                                setPhoneTypeFilters(e.target.checked
                                                    ? [...phoneTypeFilters, 'mobile']
                                                    : phoneTypeFilters.filter(f => f !== 'mobile')
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                        />
                                        <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-xs text-ink-muted">Portable 06/07</span>
                                    </label>
                                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={phoneTypeFilters.includes('landline')}
                                            onChange={(e) => {
                                                setPhoneTypeFilters(e.target.checked
                                                    ? [...phoneTypeFilters, 'landline']
                                                    : phoneTypeFilters.filter(f => f !== 'landline')
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                        />
                                        <Phone className="w-4 h-4 text-ink-muted" />
                                        <span className="text-xs text-ink-muted">Téléphone fixe</span>
                                    </label>
                                    {phoneTypeFilters.length > 0 && (
                                        <button
                                            onClick={() => setPhoneTypeFilters([])}
                                            className="w-full text-left px-3 py-1.5 text-xs text-ink-faint hover:bg-surface-sunken hover:text-ink"
                                        >
                                            Réinitialiser le type
                                        </button>
                                    )}
                                    
                                    <div className="border-t border-border my-1"></div>
                                    <div className="px-3 py-1">
                                        <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Type d'email</span>
                                    </div>
                                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={emailTypeFilters.includes('owner')}
                                            onChange={(e) => {
                                                setEmailTypeFilters(e.target.checked
                                                    ? [...emailTypeFilters, 'owner']
                                                    : emailTypeFilters.filter(f => f !== 'owner')
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                        />
                                        <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <span className="text-xs text-ink-muted">Propriétaire</span>
                                    </label>
                                    <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={emailTypeFilters.includes('business')}
                                            onChange={(e) => {
                                                setEmailTypeFilters(e.target.checked
                                                    ? [...emailTypeFilters, 'business']
                                                    : emailTypeFilters.filter(f => f !== 'business')
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                        />
                                        <svg className="w-4 h-4 text-ink-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                        <span className="text-xs text-ink-muted">Email entreprise</span>
                                    </label>
                                    {emailTypeFilters.length > 0 && (
                                        <button
                                            onClick={() => setEmailTypeFilters([])}
                                            className="w-full text-left px-3 py-1.5 text-xs text-ink-faint hover:bg-surface-sunken hover:text-ink"
                                        >
                                            Réinitialiser le type d'email
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Statut réseau */}
                        {/* <div className="relative">
                            <button
                                onClick={() => {
                                    setShowNetworkDropdown(!_showNetworkDropdown);
                                    setShowContactDropdown(false);
                                    setShowEstablishmentDropdown(false);
                                    setShowConceptDropdown(false);
                                }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                    networkStatus.length > 0
                                        ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                        : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                }`}
                            >
                                <Store className="w-4 h-4" />
                                <span>Réseau</span>
                                {networkStatus.length > 0 && (
                                    <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                        {networkStatus.length}
                                    </span>
                                )}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {_showNetworkDropdown && (
                                <div ref={networkDropdownAlign.ref} className={`absolute top-full mt-1 ${networkDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[180px] py-1`}>
                                    {_networkStatusOptions.map(option => (
                                        <label key={option.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={networkStatus.includes(option.value)}
                                                onChange={(e) => {
                                                    setNetworkStatus(e.target.checked
                                                        ? [...networkStatus, option.value]
                                                        : networkStatus.filter(s => s !== option.value)
                                                    );
                                                }}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted">{option.label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div> */}

                        {/* Type établissement */}
                        {establishmentTypeOptions.length > 0 && (
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setShowEstablishmentDropdown(!showEstablishmentDropdown);
                                        setShowContactDropdown(false);
                                        setShowNetworkDropdown(false);
                                        setShowConceptDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                        establishmentType.length > 0
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <span>Type</span>
                                    {establishmentType.length > 0 && (
                                        <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                            {establishmentType.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {showEstablishmentDropdown && (
                                    <div ref={establishmentDropdownAlign.ref} className={`absolute top-full mt-1 ${establishmentDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[200px] max-h-[280px] overflow-y-auto overflow-x-hidden ds-scrollbar py-1`}>
                                        {establishmentTypeOptions.map(option => (
                                            <label key={option.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={establishmentType.includes(option.value)}
                                                    onChange={(e) => {
                                                        setEstablishmentType(e.target.checked
                                                            ? [...establishmentType, option.value]
                                                            : establishmentType.filter(t => t !== option.value)
                                                        );
                                                    }}
                                                    className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                />
                                                <span className="text-xs text-ink-muted">{option.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Code NAF */}
                        <div className="relative">
                            <button
                                onClick={() => { if (showNafDropdown) setNafSearch(''); setShowNafDropdown(!showNafDropdown); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${nafCodeFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>NAF</span>
                                {nafCodeFilters.length > 0 && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">{nafCodeFilters.length}</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showNafDropdown && (
                                <div ref={nafDropdownAlign.ref} className={`absolute top-full mt-1 ${nafDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[240px] py-1`}>
                                    <div className="px-2 py-1.5 border-b border-border">
                                        <input
                                            type="text"
                                            value={nafSearch}
                                            onChange={(e) => setNafSearch(e.target.value)}
                                            placeholder="Rechercher un code NAF..."
                                            className="w-full text-xs px-2 py-1 border border-border rounded focus:outline-none focus:border-accent"
                                            onClick={(e) => e.stopPropagation()}
                                            autoFocus
                                        />
                                    </div>
                                    <div className="max-h-[280px] overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                        {availableNafCodes
                                            .map(({ code, description }) => ({ code, label: description ? `${code} - ${description}` : code }))
                                            .filter(({ label }) => label.toLowerCase().includes(nafSearch.toLowerCase()))
                                            .map(({ code, label }) => (
                                                <label key={code} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                    <input type="checkbox" checked={nafCodeFilters.includes(code)} onChange={(e) => handleNafToggle(code, e.target.checked)} className="w-3.5 h-3.5 rounded-sm border-border accent-accent" />
                                                    <span className="text-xs text-ink-muted">{label}</span>
                                                </label>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Taille entreprise */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowCompanySizeDropdown(!showCompanySizeDropdown); setShowNafDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${companySizeFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Taille</span>
                                {companySizeFilters.length > 0 && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">{companySizeFilters.length}</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showCompanySizeDropdown && (
                                <div ref={companySizeDropdownAlign.ref} className={`absolute top-full mt-1 ${companySizeDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-56 py-1 max-h-80 overflow-y-auto overflow-x-hidden ds-scrollbar`}>
                                    {([
                                        { header: 'TPE', ranges: ['Non employeur', '0 salarié', '1-2 salariés', '3-5 salariés', '6-9 salariés'] },
                                        { header: 'PME', ranges: ['10-19 salariés', '20-49 salariés', '50-99 salariés', '100-199 salariés', '200-249 salariés'] },
                                        { header: 'ETI', ranges: ['250-499 salariés', '500-999 salariés', '1 000-1 999 salariés', '2 000-4 999 salariés'] },
                                        { header: 'GE',  ranges: ['5 000-9 999 salariés', '10 000 salariés et plus'] },
                                    ]).map(({ header, ranges }) => (
                                        <div key={header}>
                                            <div className="px-3 pt-2 pb-0.5">
                                                <span className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">{header}</span>
                                            </div>
                                            {ranges.map(range => (
                                                <label key={range} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                    <input type="checkbox" checked={companySizeFilters.includes(range)} onChange={(e) => setCompanySizeFilters(e.target.checked ? [...companySizeFilters, range] : companySizeFilters.filter(s => s !== range))} className="w-3.5 h-3.5 rounded-sm border-border accent-accent" />
                                                    <span className="text-xs text-ink-muted">{range}</span>
                                                </label>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Forme juridique */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowLegalFormDropdown(!showLegalFormDropdown); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${legalFormFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Forme juridique</span>
                                {legalFormFilters.length > 0 && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">{legalFormFilters.length}</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showLegalFormDropdown && (
                                <div ref={legalFormDropdownAlign.ref} className={`absolute top-full mt-1 ${legalFormDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 whitespace-nowrap py-1`}>
                                    {['SAS', 'SASU', 'SARL', 'EURL', 'SA', 'Entrepreneur individuel'].map(form => (
                                        <label key={form} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input type="checkbox" checked={legalFormFilters.includes(form)} onChange={(e) => setLegalFormFilters(e.target.checked ? [...legalFormFilters, form] : legalFormFilters.filter(f => f !== form))} className="w-3.5 h-3.5 rounded-sm border-border accent-accent" />
                                            <span className="text-xs text-ink-muted">{form}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Contrôle sanitaire — résultat */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowControlResultDropdown(!showControlResultDropdown); setShowControlFollowUpDropdown(false); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${controlResultFilter ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Résultat sanitaire</span>
                                {controlResultFilter && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">1</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showControlResultDropdown && (
                                <div ref={controlResultDropdownAlign.ref} className={`absolute top-full mt-1 ${controlResultDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 whitespace-nowrap py-1`}>
                                    {['Très satisfaisant', 'Satisfaisant', 'A améliorer', 'A corriger de manière urgente'].map(val => (
                                        <label key={val} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={controlResultFilter === val}
                                                onChange={() => setControlResultFilter(controlResultFilter === val ? '' : val)}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Contrôle sanitaire — suites */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowControlFollowUpDropdown(!showControlFollowUpDropdown); setShowControlResultDropdown(false); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${controlFollowUpFilter ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Suites sanitaires</span>
                                {controlFollowUpFilter && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">1</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showControlFollowUpDropdown && (
                                <div ref={controlFollowUpDropdownAlign.ref} className={`absolute top-full mt-1 ${controlFollowUpDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 whitespace-nowrap py-1`}>
                                    {['Avertissement', 'Mise en demeure', 'Procès-verbal', 'Fermeture administrative'].map(val => (
                                        <label key={val} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={controlFollowUpFilter === val}
                                                onChange={() => setControlFollowUpFilter(controlFollowUpFilter === val ? '' : val)}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted">{val}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Âge fondateur */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowAgeDropdown(!showAgeDropdown); setShowControlResultDropdown(false); setShowControlFollowUpDropdown(false); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${ageFilter ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Âge fondateur</span>
                                {ageFilter && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">1</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showAgeDropdown && (
                                <div ref={ageDropdownAlign.ref} className={`absolute top-full mt-1 ${ageDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 whitespace-nowrap py-1`}>
                                    {[
                                        { value: '<35', label: '< 35 ans' },
                                        { value: '35-45', label: '35 – 45 ans' },
                                        { value: '45-55', label: '45 – 55 ans' },
                                        { value: '55+', label: '55+ ans' },
                                    ].map(({ value, label }) => (
                                        <label key={value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={ageFilter === value}
                                                onChange={() => setAgeFilter(ageFilter === value ? '' : value)}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Nb d'établissements */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowNbEtabDropdown(!showNbEtabDropdown); setShowAgeDropdown(false); setShowControlResultDropdown(false); setShowControlFollowUpDropdown(false); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${nbEtabFilters.length > 0 ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Nb établissements</span>
                                {nbEtabFilters.length > 0 && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">{nbEtabFilters.length}</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showNbEtabDropdown && (
                                <div ref={nbEtabDropdownAlign.ref} className={`absolute top-full mt-1 ${nbEtabDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 whitespace-nowrap py-1`}>
                                    {NB_ETAB_BUCKETS.map(({ value, label, sub }) => (
                                        <label key={value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={nbEtabFilters.includes(value)}
                                                onChange={(e) => setNbEtabFilters(e.target.checked ? [...nbEtabFilters, value] : nbEtabFilters.filter(v => v !== value))}
                                                className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                            />
                                            <span className="text-xs text-ink-muted">{label}</span>
                                            <span className="text-[11px] text-ink-faint">{sub}</span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Date d'ouverture */}
                        <div className="relative">
                            <button
                                onClick={() => { setShowOpeningDateDropdown(!showOpeningDateDropdown); setShowNbEtabDropdown(false); setShowAgeDropdown(false); setShowControlResultDropdown(false); setShowControlFollowUpDropdown(false); setShowNafDropdown(false); setShowCompanySizeDropdown(false); setShowLegalFormDropdown(false); setShowContactDropdown(false); setShowEstablishmentDropdown(false); }}
                                className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${(openingDateFrom || openingDateTo) ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90' : 'bg-surface text-ink-muted border border-border hover:border-border-strong'}`}
                            >
                                <span>Date d'ouverture</span>
                                {(openingDateFrom || openingDateTo) && <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">1</span>}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showOpeningDateDropdown && (
                                <div ref={openingDateDropdownAlign.ref} className={`absolute top-full mt-1 ${openingDateDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-64 p-3 space-y-2`}>
                                    <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-wide">Intervalle d'ouverture</p>
                                    <div className="space-y-1.5">
                                        <label className="block text-xs text-ink-muted">Du</label>
                                        <input
                                            type="date"
                                            value={openingDateFrom}
                                            onChange={(e) => setOpeningDateFrom(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="block text-xs text-ink-muted">Au</label>
                                        <input
                                            type="date"
                                            value={openingDateTo}
                                            onChange={(e) => setOpeningDateTo(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                        />
                                    </div>
                                    {(openingDateFrom || openingDateTo) && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setOpeningDateFrom(''); setOpeningDateTo(''); }}
                                            className="w-full text-left text-xs text-ink-faint hover:text-ink-muted pt-1"
                                        >
                                            Réinitialiser
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Concept */}
                        {_availableSubCategories.length > 0 && (
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        if (_showConceptDropdown) _setConceptSearch('');
                                        setShowConceptDropdown(!_showConceptDropdown);
                                        setShowContactDropdown(false);
                                        setShowNetworkDropdown(false);
                                        setShowEstablishmentDropdown(false);
                                    }}
                                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                                        concept.length > 0
                                            ? 'bg-ink text-ink-inverse border border-ink hover:bg-ink/90'
                                            : 'bg-surface text-ink-muted border border-border hover:border-border-strong'
                                    }`}
                                >
                                    <span>Concept</span>
                                    {concept.length > 0 && (
                                        <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                            {concept.length}
                                        </span>
                                    )}
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {_showConceptDropdown && (
                                    <div ref={conceptDropdownAlign.ref} className={`absolute top-full mt-1 ${conceptDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 min-w-[180px]`}>
                                        <div className="px-2 py-1.5 border-b border-border">
                                            <input
                                                type="text"
                                                value={_conceptSearch}
                                                onChange={(e) => _setConceptSearch(e.target.value)}
                                                placeholder="Rechercher..."
                                                className="w-full text-xs px-2 py-1 border border-border rounded focus:outline-none focus:border-accent"
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-[240px] overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                            {_availableSubCategories.filter(c => c.toLowerCase().includes(_conceptSearch.toLowerCase())).map(c => (
                                                <label key={c} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={concept.includes(c)}
                                                        onChange={(e) => {
                                                            setConcept(e.target.checked
                                                                ? [...concept, c]
                                                                : concept.filter(x => x !== c)
                                                            );
                                                        }}
                                                        className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                                    />
                                                    <span className="text-xs text-ink-muted">{c}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Clear */}
                        {activeFilterCount > 0 && (
                            <button
                                onClick={clearAllFilters}
                                className="h-8 px-2.5 text-xs font-medium text-ink-muted hover:text-danger hover:bg-danger-subtle border border-border hover:border-danger/30 rounded-lg transition-colors flex items-center gap-1"
                            >
                                <X className="w-3.5 h-3.5" />
                                Réinitialiser
                            </button>
                        )}

                        {/* Save as list */}
                        <button
                            ref={saveButtonRef}
                            onClick={() => setShowSaveModal(true)}
                            className="ml-auto h-8 px-3 text-xs font-medium bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors duration-150 flex items-center gap-1.5 shrink-0"
                        >
                            <Bookmark className="w-3.5 h-3.5" />
                            Enregistrer comme liste
                        </button>

                        {/* Export CSV — duckflow admins only */}
                        {isDuckflowAdmin && (
                            <button
                                onClick={() => { setExportError(""); setExportLimit("200"); setExportCleanLines(""); setExportOpeningDateEnabled(false); setExportMinOpeningDate(""); setExportCreationDateEnabled(false); setExportMinCreationDate(""); setExportHygieneControlEnabled(false); setExportHygieneControlDate(""); setShowExportModal(true); }}
                                className="h-8 px-3 text-xs font-medium bg-surface text-ink-muted border border-border-strong rounded-lg hover:border-border-strong hover:bg-surface-sunken transition-colors flex items-center gap-1.5 shrink-0"
                            >
                                <Download className="w-3.5 h-3.5" />
                                Exporter CSV
                            </button>
                        )}
                    </div>
                </div>

                {/* Results */}
                {loading ? (
                    <SignalTableSkeleton rows={10} />
                ) : establishments.length > 0 ? (
                    <>
                        <SignalTable
                            signals={establishments as any[]}
                            onSaveToggle={handleSaveToggle}
                            phoneTypeFilter={phoneTypeFilters}
                            emailTypeFilter={emailTypeFilters}
                            nbEtabBuckets={nbEtabFilters}
                            activeSignalTypes={['restaurant_opening']}
                            dateSortOrder={openingDateSort}
                            onDateSortClick={cycleOpeningDateSort}
                            onOwnersEnriched={fetchEstablishments}
                        />

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between bg-surface rounded-2xl border border-border px-4 py-3">
                                <div className="text-xs text-ink-faint">
                                    <span className="font-medium text-ink">{((page - 1) * itemsPerPage) + 1}</span>
                                    {' – '}
                                    <span className="font-medium text-ink">{Math.min(page * itemsPerPage, total)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => goToPage(page - 1)}
                                        disabled={page === 1}
                                        className="p-1.5 rounded bg-surface-sunken hover:bg-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft className="w-4 h-4 text-ink-muted" />
                                    </button>
                                    <div className="flex items-center gap-0.5">
                                        {getPageNumbers().map((pageNum, index) => (
                                            pageNum === '...' ? (
                                                <span key={`ellipsis-${index}`} className="px-2 text-ink-faint">...</span>
                                            ) : (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => goToPage(pageNum as number)}
                                                    className={`min-w-[32px] h-8 px-2 rounded font-medium text-xs transition-colors ${
                                                        page === pageNum
                                                            ? 'bg-ink text-ink-inverse'
                                                            : 'bg-surface-sunken text-ink-muted hover:bg-border'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            )
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => goToPage(page + 1)}
                                        disabled={page === totalPages}
                                        className="p-1.5 rounded bg-surface-sunken hover:bg-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronRight className="w-4 h-4 text-ink-muted" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center h-96 bg-surface border border-border rounded-2xl">
                        <Store className="w-16 h-16 text-ink-faint mb-4" />
                        <p className="text-lg font-medium text-ink mb-2">Aucun établissement trouvé</p>
                        {searchQuery ? (
                            <>
                                <p className="text-sm text-ink-faint mb-4">Essayez avec un autre terme de recherche</p>
                                <button
                                    onClick={() => { setSearchQuery(""); setPage(1); }}
                                    className="px-4 py-2 bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 text-sm font-medium transition-colors"
                                >
                                    Réinitialiser la recherche
                                </button>
                            </>
                        ) : (
                            <p className="text-sm text-ink-faint">Aucun établissement disponible pour le moment</p>
                        )}
                    </div>
                )}
            </div>
        </div>
        {showTrialWall && <TrialPageWall onClose={() => setShowTrialWall(false)} />}

        {/* Export CSV modal */}
        {showExportModal && (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowExportModal(false)}
            >
                <div
                    className="bg-surface rounded-2xl w-full max-w-md"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                        <div className="flex items-center gap-2">
                            <Download className="w-4 h-4 text-ink-muted" />
                            <h2 className="text-sm font-semibold text-ink">Exporter les établissements</h2>
                        </div>
                        <button onClick={() => setShowExportModal(false)} className="p-1 rounded text-ink-faint hover:text-ink-muted transition-colors">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="p-6 space-y-5">
                        {/* Active filters summary */}
                        <div className="bg-surface-sunken border border-border rounded-md px-3 py-2.5 space-y-1">
                            <p className="text-xs font-semibold text-ink-muted">Filtres actifs appliqués à l'export</p>
                            <p className="text-xs text-ink-faint">
                                {activeFilterCount > 0
                                    ? `${activeFilterCount} filtre${activeFilterCount > 1 ? "s" : ""} · ${total.toLocaleString("fr-FR")} établissements correspondants`
                                    : `Aucun filtre — ${total.toLocaleString("fr-FR")} établissements au total`}
                            </p>
                            <p className="text-[11px] text-ink-faint">Limité à {exportLimit || "200"} lignes.</p>
                        </div>

                        {/* Export limit */}
                        <div>
                            <label className="text-xs font-semibold text-ink-muted mb-1.5 block">Nombre de lignes à exporter</label>
                            <input
                                type="number"
                                min={1}
                                max={250000}
                                value={exportLimit}
                                onChange={(e) => setExportLimit(e.target.value)}
                                className="w-full px-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-accent"
                            />
                        </div>

                        {/* Lignes propres */}
                        <div>
                            <label className="text-xs font-semibold text-ink-muted mb-1.5 block">Lignes propres (non masquées)</label>
                            <input
                                type="number"
                                min={0}
                                placeholder="Toutes (aucun masquage)"
                                value={exportCleanLines}
                                onChange={(e) => setExportCleanLines(e.target.value)}
                                className="w-full px-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-accent"
                            />
                            {exportCleanLines !== "" && (
                                <p className="text-[11px] text-ink-faint mt-1">
                                    Les {parseInt(exportCleanLines) || 0} premières lignes seront complètes. Les suivantes auront email, téléphone, LinkedIn et SIREN partiellement masqués avec des *.
                                </p>
                            )}
                        </div>

                        {/* Colonnes exportées */}
                        <div>
                            <p className="text-xs font-semibold text-ink-muted mb-1.5">Colonnes exportées</p>
                            <div className="flex flex-wrap gap-1">
                                {["Prénom", "Nom", "Âge", "Nb établissements", "LinkedIn fondateur", "Note Google", "Avis Google", "Adresse", "Ville", "Code postal", "Population", "Prix m²", "Nom restaurant", "Email", "Tél. mobile", "Tél. fixe", "SIREN", "Code NAF", "Nb employés", "Site web", "Date ouverture", "Date contrôle hygiène", "Résultat contrôle hygiène", "Suites contrôle hygiène", "Date changement propriétaire"].map(col => (
                                    <span key={col} className="px-2 py-0.5 bg-surface-sunken text-ink-muted rounded text-[11px]">{col}</span>
                                ))}
                            </div>
                        </div>

                        {/* Extra filters */}
                        <div className="space-y-3">
                            <p className="text-xs font-semibold text-ink-muted">Filtres supplémentaires</p>

                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={exportOpeningDateEnabled}
                                        onChange={(e) => { setExportOpeningDateEnabled(e.target.checked); if (!e.target.checked) setExportMinOpeningDate(""); }}
                                        className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                    />
                                    <span className="text-xs text-ink-muted">Date d'ouverture minimale</span>
                                </label>
                                {exportOpeningDateEnabled && (
                                    <input
                                        type="date"
                                        value={exportMinOpeningDate}
                                        onChange={(e) => setExportMinOpeningDate(e.target.value)}
                                        className="w-full px-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-accent"
                                    />
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={exportCreationDateEnabled}
                                        onChange={(e) => { setExportCreationDateEnabled(e.target.checked); if (!e.target.checked) setExportMinCreationDate(""); }}
                                        className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                    />
                                    <span className="text-xs text-ink-muted">Date de création (SIREN) minimale</span>
                                </label>
                                {exportCreationDateEnabled && (
                                    <input
                                        type="date"
                                        value={exportMinCreationDate}
                                        onChange={(e) => setExportMinCreationDate(e.target.value)}
                                        className="w-full px-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-accent"
                                    />
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={exportHygieneControlEnabled}
                                        onChange={(e) => { setExportHygieneControlEnabled(e.target.checked); if (!e.target.checked) setExportHygieneControlDate(""); }}
                                        className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                    />
                                    <span className="text-xs text-ink-muted">Contrôle sanitaire récent (depuis le)</span>
                                </label>
                                {exportHygieneControlEnabled && (
                                    <input
                                        type="date"
                                        value={exportHygieneControlDate}
                                        onChange={(e) => setExportHygieneControlDate(e.target.value)}
                                        className="w-full px-3 py-1.5 text-xs rounded-lg border border-border focus:outline-none focus:border-accent"
                                    />
                                )}
                            </div>
                        </div>

                        {exportError && (
                            <p className="text-xs text-red-600">{exportError}</p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
                        <button
                            onClick={() => setShowExportModal(false)}
                            className="px-4 py-2 text-sm border border-border text-ink-muted rounded-md hover:bg-surface-sunken transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={exporting}
                            className="px-4 py-2 text-sm bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                            <Download className="w-3.5 h-3.5" />
                            {exporting ? "Export en cours..." : "Télécharger le CSV"}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Save as list modal */}
        {showSaveModal && (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowSaveModal(false)}
            >
                <div
                    className="bg-surface rounded-2xl w-full max-w-md"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                        <h2 className="text-sm font-semibold text-ink">Enregistrer comme liste</h2>
                        <button
                            onClick={() => setShowSaveModal(false)}
                            className="p-1 rounded text-ink-faint hover:text-ink-muted transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-ink-muted mb-1">
                                Nom de la liste <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={saveListName}
                                onChange={(e) => setSaveListName(e.target.value)}
                                placeholder="Ex : Restaurants Paris avec email"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-border focus:outline-none focus:border-accent"
                                autoFocus
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-ink-muted mb-1">Description</label>
                            <input
                                type="text"
                                value={saveListDescription}
                                onChange={(e) => setSaveListDescription(e.target.value)}
                                placeholder="Optionnelle"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-border focus:outline-none focus:border-accent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-ink-muted mb-2">Type de liste</label>
                            <div className="grid grid-cols-2 gap-2">
                                {([
                                    { key: "dynamic", label: "Dynamique", desc: "Met à jour automatiquement avec les nouveaux résultats" },
                                    { key: "static", label: "Statique", desc: "Fige les résultats au moment de la création" },
                                ] as const).map((opt) => (
                                    <button
                                        key={opt.key}
                                        onClick={() => setSaveListType(opt.key)}
                                        className={`p-3 rounded-md border text-left transition-colors ${
                                            saveListType === opt.key
                                                ? "border-ink bg-surface-sunken"
                                                : "border-border hover:border-border-strong"
                                        }`}
                                    >
                                        <div className="text-xs font-semibold text-ink mb-1">{opt.label}</div>
                                        <div className="text-xs text-ink-faint leading-tight">{opt.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {activeFilterCount > 0 && (
                            <div className="bg-surface-sunken border border-border rounded-md px-3 py-2">
                                <p className="text-xs text-ink-muted">
                                    {activeFilterCount} filtre{activeFilterCount > 1 ? "s" : ""} actif{activeFilterCount > 1 ? "s" : ""} · {total.toLocaleString("fr-FR")} résultats inclus
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 px-6 py-4 border-t border-border">
                        <button
                            onClick={() => setShowSaveModal(false)}
                            className="px-4 py-2 text-sm border border-border text-ink-muted rounded-md hover:bg-surface-sunken transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSaveList}
                            disabled={!saveListName.trim() || savingList}
                            className="px-4 py-2 text-sm bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {savingList ? "Création..." : "Créer la liste"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

